import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import {
  ChatBubbleOutlineOutlined as MessageIcon,
  DraftsOutlined as EmailIcon,
  ChatOutlined as ChatIcon,
  AddIcCall as CallIcon,
  Link as PaymentIcon,
  Forum as FrontIcon,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import moment from 'moment';

import { getQueryVariable } from '../../../../../utils/queryParams';
import Lead, {
  ContactProvider,
  LeadHistoryType,
  SalesStage,
} from '../../../../Model/Lead';
import { getFormAnswersFromData } from '../../../../utils/formAnswers';
import DialogModal from '../../../../../components/DialogModal';
import ContactInfoItem from '../../../ClientInfo/components/ContactInfoItem';
import {
  StyledEmailIcon,
  StyledPhoneIcon,
  StyledTimeZoneIcon,
} from '../../../ClientInfo/components/UserInfoSection/styles';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import SplitButton from '../../../../../components/SplitButton';
import MsgTemplate from '../../../../../Model/MsgTemplate';
import FirebaseContext from '../../../../../context/FirebaseContext';
import { getNextSalesStage, rescheduleViaSDR } from '../../../../utils/lead';
import MsgEndpointContext, {
  withMsgEndpointContextProvider,
} from '../../../../context/MsgEndpointContext';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../LoadingOverlay';
import MsgModal from '../MsgModal';
import ChatModal from '../ChatModal';
import CallLogModal from '../CallLogModal';
import ReasonModal from '../ReasonModal';
import CalDotComMeetingCancelModal from '../CalDotComMeetingCancelModal';
import {
  createTryRazContact,
  getFrontConversationId,
  getFrontLink,
  getTryRazConversationLink,
} from '../../utils';
import { LeadContractModal } from '../../../ContractModal';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import salesStageTexts from '../../salesStages.json';
import { getTabsConfig, tabs } from './tabs/tabs';
import {
  Answer,
  ContactInfoSection,
  ModalContent,
  LeadInfoSection,
  Question,
  Title,
  TitleSection,
  ActionButton,
  QuestionsContainer,
  MainSection,
  RightSection,
  Container,
  ButtonsContainer,
  TabHeaderRow,
  StyledTabs,
} from './styles';
import texts from './texts.json';

const EMPTY_CELL = '-';

const LeadsModal = ({
  lead,
  isInsideSalesView,
  onClose,
  showModal,
  userId,
  userName,
  coach,
  requireOutcome: requireOutcomeProp,
}) => {
  const [selectedMsgProvider, setSelectedMsgProvider] = useState(null);
  const [selectedMsgTemplate, setSelectedMsgTemplate] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showCallLogModal, setShowCallLogModal] = useState(false);
  const [showRescheduledCallModal, setShowRescheduledCallModal] = useState(false);
  const [showRescheduleViaSDRModal, setShowRescheduleViaSDRModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  // restrict closing the modal until the outcome is saved
  const [requireOutcome, setRequireOutcome] = useState(requireOutcomeProp);
  const [showCalDotComMeetingCancelModal, setShowCalDotComMeetingCancelModal] = useState(false);

  const [history, setHistory] = useState([]);
  const [msgTemplates, setMsgTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(tabs.HISTORY);

  const isComponentMountedRef = useComponentMounted();
  const { msgEndpointCollection } = useContext(MsgEndpointContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();

  const showOutreachOptions = useMemo(() => getQueryVariable('isSuperUser') === 'true', []);

  useEffect(() => {
    const loadMsgTemplates = async () => {
      if (coach) {
        const templates = await MsgTemplate.getTemplatesByCoach(coach.id);
        if (isComponentMountedRef.current) {
          setMsgTemplates(templates.docs);
        }
      }
    };
    loadMsgTemplates();
  }, [
    isComponentMountedRef,
    coach,
  ]);

  const nextMsgTemplate = useMemo(() => {
    const nextSalesStage = getNextSalesStage(lead.salesStage);
    let nextTemplate = null;
    if (nextSalesStage) {
      nextTemplate = msgTemplates.find((template) => template.salesStage === nextSalesStage);
    }
    // if there is no msg template for next sales stage, we should use 2nd msg template as default one
    // this is because we don't have msg template for some sales stages (e.g. 1st cold call)
    if (!nextTemplate) {
      nextTemplate = msgTemplates.find((template) => template.salesStage === SalesStage.SECOND_MESSAGE);
    }
    return nextTemplate;
  }, [lead.salesStage, msgTemplates]);

  useEffect(() => {
    const loadHistory = async () => {
      if (lead) {
        setHistory([]);
        const historyData = await lead?.getHistory();
        if (isComponentMountedRef.current) {
          setHistory(historyData);
        }
      }
    };
    loadHistory();
  }, [isComponentMountedRef, lead]);

  const onCloseModal = useCallback(() => {
    if (requireOutcome) {
      showToast(texts.outcomeNotRecorded, { error: true });
    } else {
      onClose();
    }
  }, [requireOutcome, showToast, onClose]);

  const tabsConfig = useMemo(() => getTabsConfig(isInsideSalesView), [isInsideSalesView]);

  const tabContent = useMemo(() => {
    const { component: TabContent, props } = tabsConfig[selectedTab];

    let extraProps = {};
    if (selectedTab === tabs.HISTORY) {
      extraProps = {
        lead,
        isInsideSalesView,
        history,
        showCallLogModal: setShowCallLogModal,
        onCloseLeadModal: () => {
          setRequireOutcome(false);
          onClose();
        },
      };
    } else if (selectedTab === tabs.COACH_INFO) {
      extraProps = {
        coachId: coach?.id || lead.coach,
        coachName: coach?.label || '',
      };
    }
    return (
      <TabContent
        {...extraProps}
        {...props}
      />
    );
  }, [
    coach,
    history,
    isInsideSalesView,
    lead,
    selectedTab,
    tabsConfig,
    onClose,
  ]);

  const formattedResponses = useMemo(() => getFormAnswersFromData(lead), [lead]);
  const msgActions = useMemo(() => msgTemplates.map((template) => (
    {
      key: template.id,
      label: `Send ${template.name}`,
    }
  )), [msgTemplates]);

  const showMsgModal = useMemo(() => !!selectedMsgProvider || !!selectedMsgTemplate,
    [selectedMsgProvider, selectedMsgTemplate]);

  // Check if we should show the cancel Cal.com meeting button
  const shouldShowCancelCalDotComButton = useMemo(() => !!lead.calendlyMeetingTime
    && moment(lead.calendlyMeetingTime.toDate()).isAfter(moment()), [lead]);

  const onMsgModalClose = useCallback(() => {
    setSelectedMsgProvider(null);
    setSelectedMsgTemplate(null);
  }, []);

  const clientFirstName = useMemo(() => lead?.name?.split(' ')[0], [lead]);

  const onMsgSent = useCallback((msgData) => {
    const {
      msgTemplate,
    } = msgData;
    // update lead sales stage
    if (msgTemplate && msgTemplate.salesStage) {
      lead.setSalesStage(msgTemplate.salesStage, { userId, userName });
    }
    // add lead history event
    lead.addItemToHistory({
      value: {
        ...msgData,
        msgTemplate: msgTemplate?.data || {},
      },
      activityType: LeadHistoryType.MSG,
      createdBy: userId,
      createdByUserName: userName,
    });
  }, [
    lead,
    userId,
    userName,
  ]);

  const { lastContactNo, lastProvider } = useMemo(() => {
    if (lead.lastContactedMsgProvider && lead.phoneNumber) {
      return {
        lastContactNo: lead.phoneNumber,
        lastProvider: lead.lastContactedMsgProvider,
      };
    }
    const lastMsgHistory = history.splice().reverse().find((item) => item.activityType === LeadHistoryType.MSG);
    if (lastMsgHistory) {
      return {
        lastContactNo: lastMsgHistory.value.contactNo,
        lastProvider: lastMsgHistory.value.msgProvider,
      };
    }
    return {};
  }, [history, lead]);

  const onRescheduleViaSDR = useCallback(async ({ reason }) => {
    await rescheduleViaSDR(lead, reason, userId, userName);
  }, [lead, userId, userName]);

  const markLeadAsNoShow = useCallback(async () => {
    // Add a new entry to the lead history to track who marked the lead.
    await lead.addItemToHistory({
      value: texts.noShowValue,
      createdAt: new Date(),
      activityType: LeadHistoryType.NO_SHOW,
      createdBy: userId,
      createdByUserName: userName,
    });

    // Set lead's no show flag.
    await lead.setNoShow();
  }, [
    lead,
    userId,
    userName,
  ]);

  const onCallButtonClick = useCallback(async () => {
    setIsLoading(true);
    const { success, error } = await createTryRazContact(remote, lead.id);
    if (success) {
      // open tryraz in new tab
      window.open(getTryRazConversationLink(lead.phoneNumber), '_blank');
      await userDoc.updateIsISOnCall(false);
      if (isComponentMountedRef.current) {
        setRequireOutcome(true);
      }
    } else {
      showToast(error || texts.somethingWentWrong, { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    isComponentMountedRef,
    lead,
    remote,
    showToast,
    setRequireOutcome,
    userDoc,
  ]);

  const onOpenFrontButtonClick = useCallback(async () => {
    setIsLoading(true);
    const { success, error, conversationId } = await getFrontConversationId(remote, lead.frontContactId);
    if (success && conversationId) {
      // open front in new tab
      window.open(getFrontLink(conversationId), '_blank');
    } else {
      showToast(error || texts.somethingWentWrong, { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    isComponentMountedRef,
    lead,
    remote,
    showToast,
  ]);

  return (
    <DialogModal
      open={showModal}
      onClose={onCloseModal}
      fullWidth
      maxWidth="lg"
      aria-modal="true"
    >
      {showMsgModal && (
        <MsgModal
          open={showMsgModal}
          onClose={onMsgModalClose}
          contactNo={lastContactNo}
          provider={selectedMsgProvider || lastProvider}
          msgTemplate={selectedMsgTemplate}
          msgParams={{ clientName: clientFirstName, coachName: coach.label }}
          onMsgSent={onMsgSent}
          msgEndpoints={msgEndpointCollection?.docs || []}
          sendAsUserId={lead.lastSendAsUserId}
          leadId={lead.id}
        />
      )}
      {showChatModal && (
        <ChatModal
          open={showChatModal}
          onClose={() => setShowChatModal(false)}
          contactNo={lastContactNo}
          name={lead.name}
          sendAsUserId={lead.lastSendAsUserId}
          sendAsUserName={lead.lastSendAs}
          email={lead.email}
          provider={lastProvider}
          onMoreSelected={(provider = ContactProvider.iMessage) => setSelectedMsgProvider(provider)}
          msgEndpoints={msgEndpointCollection?.docs || []}
          onMsgSent={onMsgSent}
          showInputField={showOutreachOptions}
        />
      )}
      {showCallLogModal && (
        <CallLogModal
          showModal={showCallLogModal}
          onClose={() => setShowCallLogModal(false)}
          lead={lead}
          userId={userId}
          userName={userName}
        />
      )}
      {showRescheduledCallModal && (
        <CallLogModal
          showModal={showRescheduledCallModal}
          onClose={() => setShowRescheduledCallModal(false)}
          lead={lead}
          userId={userId}
          userName={userName}
          logRescheduledCall
        />
      )}
      {showRescheduleViaSDRModal && (
        <ReasonModal
          title={texts.rescheduleViaSDR}
          onSave={onRescheduleViaSDR}
          onClose={() => setShowRescheduleViaSDRModal(false)}
          showModal={showRescheduleViaSDRModal}
        />
      )}
      {showContractModal && (
        <LeadContractModal
          lead={lead}
          showModal
          onClose={() => setShowContractModal(false)}
          isISView={isInsideSalesView}
          showPostCommitmentConfiguration={isInsideSalesView}
        />
      )}
      {showCalDotComMeetingCancelModal && (
        <CalDotComMeetingCancelModal
          leadId={lead.id}
          onClose={() => setShowCalDotComMeetingCancelModal(false)}
          setLoading={setIsLoading}
        />
      )}
      <Container>
        <MainSection>
          <LeadInfoSection>
            <TitleSection>
              <Title>
                {lead.name || EMPTY_CELL}
              </Title>

              {isInsideSalesView && <Chip label={salesStageTexts[lead?.salesStage]} color="error" variant="outlined" />}
              {showOutreachOptions && isInsideSalesView && !!msgTemplates?.length && (
                <SplitButton
                  options={msgActions}
                  onClick={(templateIndex) => setSelectedMsgTemplate(msgTemplates[templateIndex])}
                  initialSelectedIndex={msgTemplates.indexOf(nextMsgTemplate)}
                />
              )}
            </TitleSection>
            <ContactInfoSection>
              <ContactInfoItem icon={<StyledPhoneIcon />}>
                {lead?.phoneNumber || EMPTY_CELL}
              </ContactInfoItem>
              <ContactInfoItem icon={<StyledEmailIcon />}>
                {lead?.email || EMPTY_CELL}
              </ContactInfoItem>
              <ContactInfoItem icon={<StyledTimeZoneIcon />}>
                {`${lead?.timezone || EMPTY_CELL} (${lead?.country || EMPTY_CELL})`}
              </ContactInfoItem>
              {isInsideSalesView && (
                <ContactInfoItem icon="IS">
                  {lead?.msgServerContactNo || lead?.sendBlueName || EMPTY_CELL}
                </ContactInfoItem>
              )}
            </ContactInfoSection>
          </LeadInfoSection>
          <ModalContent>
            <ButtonsContainer>
              {isInsideSalesView && (
                <>
                  <ActionButton
                    variant="outlined"
                    startIcon={<ChatIcon />}
                    provider="chat"
                    onClick={() => setShowChatModal(true)}
                  >
                    {texts.chat}
                  </ActionButton>
                  <ActionButton
                    variant="outlined"
                    startIcon={<CallIcon />}
                    provider="chat"
                    onClick={onCallButtonClick}
                  >
                    {texts.call}
                  </ActionButton>
                  {!!lead.frontContactId && (
                    <ActionButton
                      variant="outlined"
                      startIcon={<FrontIcon />}
                      provider="front"
                      onClick={onOpenFrontButtonClick}
                    >
                      {texts.frontConversation}
                    </ActionButton>
                  )}
                  {showOutreachOptions && (
                    <ActionButton
                      variant="outlined"
                      startIcon={<MessageIcon />}
                      provider="msg"
                      onClick={() => setSelectedMsgProvider(lastProvider || ContactProvider.iMessage)}
                    >
                      {texts.sendMsg}
                    </ActionButton>
                  )}
                </>
              )}
              {showOutreachOptions && (
                <ActionButton
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  provider="email"
                  href={`mailto:${lead?.email}`}
                >
                  {texts.sendEmail}
                </ActionButton>
              )}
              <ActionButton
                variant="outlined"
                startIcon={<PaymentIcon />}
                provider="paymentLink"
                onClick={() => setShowContractModal(true)}
              >
                {texts.paymentLink}
              </ActionButton>
            </ButtonsContainer>

            {isInsideSalesView && lead.salesStage === SalesStage.CALENDLY_SCHEDULED && (
              <ButtonsContainer>
                <ActionButton
                  variant="outlined"
                  provider="sentToReschedule" // TODO: standardize buttons to avoid using provider as type
                  onClick={() => setShowRescheduleViaSDRModal(true)}
                >
                  {texts.rescheduleViaSDR}
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  provider="rescheduledManually" // TODO: standardize buttons to avoid using provider as type
                  onClick={() => setShowRescheduledCallModal(true)}
                >
                  {texts.rescheduledManually}
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  provider="noShow" // TODO: standardize buttons to avoid using provider as type
                  onClick={markLeadAsNoShow}
                >
                  {texts.noShow}
                </ActionButton>
                {shouldShowCancelCalDotComButton && (
                  <ActionButton
                    variant="outlined"
                    provider="cancelMeeting"
                    onClick={() => setShowCalDotComMeetingCancelModal(true)}
                  >
                      {texts.cancelCalDotComMeeting}
                  </ActionButton>
                )}
              </ButtonsContainer>
            )}
            <h3>{texts.leadForm}</h3>
            <ol>
              <QuestionsContainer key="lead-id">
                <Question>{texts.leadId}</Question>
                <Answer>{lead.id}</Answer>
              </QuestionsContainer>
              {formattedResponses && formattedResponses.map(({ question, answer }) => !!answer
                && (
                  <QuestionsContainer key={question}>
                    <Question>{question}</Question>
                    <Answer>{answer}</Answer>
                  </QuestionsContainer>
                ))}
            </ol>
          </ModalContent>
        </MainSection>
        <RightSection>
          <TabHeaderRow>
            <StyledTabs
              tabsConfig={tabsConfig}
              selectedTab={selectedTab}
              onSelectTab={setSelectedTab}
            />
          </TabHeaderRow>
          {tabContent}
        </RightSection>
      </Container>
      <LoadingOverlay isLoading={isLoading} />
    </DialogModal>
  );
};

LeadsModal.propTypes = {
  lead: PropTypes.instanceOf(Lead).isRequired,
  onClose: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  isInsideSalesView: PropTypes.bool,
  coach: PropTypes.object,
  requireOutcome: PropTypes.bool,
};

LeadsModal.defaultProps = {
  isInsideSalesView: false,
  coach: null,
  requireOutcome: false,
};

export default compose(
  withMsgEndpointContextProvider,
  observer,
)(LeadsModal);
